import { createFileRoute, useNavigate } from '@tanstack/react-router'
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { LeadCardData } from '../../../types'
import { CampaignIndex } from '../../../components/CampaignIndex'
import { useStageReveal } from '../../../components/UniBox'
import { useRegistrationStatus } from '../../../hooks/useRegistrationStatus'
import { useAuth } from '../../../contexts/AuthContext'
import { useQueryClient } from '@tanstack/react-query'
import { useCampaigns } from '../../../hooks/useCampaigns'
import { useClientProfile } from '../../../hooks/useClientProfile'
import { useCampaignCounts } from '../../../hooks/useCampaignCounts'
import { useUserPreferences } from '../../../hooks/useUserPreferences'
import { useScrollRestore } from '../../../hooks/useScrollRestore'
import { queryKeys } from '../../../services/query/queryKeys'
import { appwriteQueryFns } from '../../../services/query/appwriteQueryFns'
import { getPreferredStatus } from '../../../utils/statusMapping'
import BuyCreditsModal from '../../../components/modals/BuyCreditsModal'
import InvoicesModal from '../../../components/modals/InvoicesModal'
import { AppLayout } from '../../../components/layouts/AppLayout'
import { TeamWorkspace } from '../../../components/admin/TeamWorkspace'
import { brainiacsService } from '../../../components/core/services/brainiacs.service'
import BrainiacsLoader from '../../../components/atoms/BrainiacsLoader'
import LoaderOverlay from '../../../components/atoms/LoaderOverlay'

type CampaignsPageSearch = {
  // Team admin toggle ('campaigns' vs 'team'). Only emitted when explicitly set
  // so it doesn't leak into every URL (the old code always serialized
  // view=campaigns, which is why login/signup showed ?view=campaigns).
  view?: 'campaigns' | 'team'
  teamTab?: 'campaigns' | 'members' | 'sellers'
  // Top-level module tab.
  tab?: 'leads' | 'sales' | 'ai-workers' | 'nexus'
  // Leads layout mode (Leads Intelligence card/list toggle).
  layout?: 'list' | 'grid' | 'cards'
  // UniBox lens (primary nav): pipeline / outreach / inbox / all.
  ln?: 'pipeline' | 'outreach' | 'inbox' | 'all'
  // UniBox view + mailbox sub-view + calendar range (Sales Intelligence /
  // AI Workers). Kept optional so deep links are short.
  v?: 'inbox' | 'calendar' | 'kanban'
  m?: 'inbox' | 'queue'
  c?: 'month' | 'week'
  // Re-open the ISR drawer for this lead after returning from the Report
  // Generator (the generator's Close navigates back with these set).
  openLeadId?: string
  openCampaignId?: string
}

/** Map the URL layout token to CampaignIndex's internal mode. */
const LAYOUT_MAP: Record<'list' | 'grid' | 'cards', 'list' | 'grid-simple' | 'grid-viewcard'> = {
  list: 'list',
  grid: 'grid-simple',
  cards: 'grid-viewcard',
}

export const Route = createFileRoute('/campaigns/')({
  validateSearch: (search: Record<string, unknown>): CampaignsPageSearch => ({
    // BOTH explicit values must survive. Collapsing 'campaigns' to undefined
    // makes the Team View toggle a dead click for team admins: the toggle writes
    // ?view=campaigns, the validator erases it, and viewMode falls back to the
    // admin default ('team'). Undefined means "no explicit choice — use the
    // default", so only a genuinely absent/invalid value may become undefined.
    view: search.view === 'team' ? 'team' : search.view === 'campaigns' ? 'campaigns' : undefined,
    // The router's default search parser JSON-parses values: a numeric id
    // ("78780") round-trips as a NUMBER. Accept both string and number here —
    // rejecting the number silently dropped openLeadId on return from the
    // Report Generator and the ISR drawer never re-opened.
    openLeadId: (typeof search.openLeadId === 'string' || typeof search.openLeadId === 'number') && /^[\w-]{1,36}$/.test(String(search.openLeadId)) ? String(search.openLeadId) : undefined,
    openCampaignId: (typeof search.openCampaignId === 'string' || typeof search.openCampaignId === 'number') && /^[\w-]{1,36}$/.test(String(search.openCampaignId)) ? String(search.openCampaignId) : undefined,
    teamTab: ['campaigns', 'members', 'sellers'].includes(search.teamTab as string) ? search.teamTab as CampaignsPageSearch['teamTab'] : undefined,
    tab: ['leads', 'sales', 'ai-workers', 'nexus'].includes(search.tab as string) ? (search.tab as CampaignsPageSearch['tab']) : undefined,
    layout: ['list', 'grid', 'cards'].includes(search.layout as string) ? (search.layout as CampaignsPageSearch['layout']) : undefined,
    ln: ['pipeline', 'outreach', 'inbox', 'all'].includes(search.ln as string) ? (search.ln as CampaignsPageSearch['ln']) : undefined,
    v: ['inbox', 'calendar', 'kanban'].includes(search.v as string) ? (search.v as CampaignsPageSearch['v']) : undefined,
    m: ['inbox', 'queue'].includes(search.m as string) ? (search.m as CampaignsPageSearch['m']) : undefined,
    c: ['month', 'week'].includes(search.c as string) ? (search.c as CampaignsPageSearch['c']) : undefined,
  }),
  component: CampaignsPage,
})

function CampaignsPage() {
  "use memo";
  const navigate = useNavigate()
  const { view, teamTab, tab, layout, ln, v, m, c, openLeadId, openCampaignId } = Route.useSearch()
  const { currentUser, targetEmail: impersonatedEmail, isTeamAdmin } = useAuth()
  const queryClient = useQueryClient()

  // State for modals
  const [isBuyCreditsModalOpen, setIsBuyCreditsModalOpen] = useState(false)
  const [isInvoicesModalOpen, setIsInvoicesModalOpen] = useState(false)
  const [isLoadingLead, setIsLoadingLead] = useState(false)
  const [campaignIndexKey, setCampaignIndexKey] = useState(0)
  // DEFAULT VIEW: team admins (and superadmins) land directly in Team View —
  // one less click to where they work. Explicit ?view= still wins, so a user
  // who switched to My Campaigns and reloads there stays there.
  // (Same identity rules as effectiveIsTeamAdmin below: the impersonated
  // user's labels when impersonating, otherwise the caller's.)
  const defaultView: 'campaigns' | 'team' = isTeamAdmin ? 'team' : 'campaigns'
  const viewMode: 'campaigns' | 'team' =
    view ? (view === 'team' ? 'team' : 'campaigns')
    : defaultView

  // GSAP "liquid reveal" loading FX. Stage remounts on top-level tab change so
  // the blur-in re-fires when switching module (leads ↔ sales ↔ nexus ↔ ...).
  // revealOnFirstMount so the reveal plays on first load too, not just switches.
  const stageRef = useStageReveal(tab ?? 'default', { revealOnFirstMount: true })

  // Registration state — driven by the TanStack `useRegistrationStatus` query
  // (single source of truth, shared with `useCampaigns`). Unregistered users
  // are redirected to /register (the form's own URL).
  const { isRegistered, isError: isRegistrationCheckFailed, isLoading: isCheckingRegistration } = useRegistrationStatus()

  // Use impersonated email if set (admin viewing as another user), otherwise current user's email
  const targetEmail = impersonatedEmail || currentUser?.email

  // TanStack Query hooks - campaigns is the single source of truth (only 1 N8N call)
  const clientQuery = useClientProfile(targetEmail)
  const prefsQuery = useUserPreferences()
  const campaignsQuery = useCampaigns(targetEmail)

  // Impersonation support: resolve the impersonated user's identity (uid + labels)
  // so we can mirror their exact view (campaign filtering + Team View access).
  const isImpersonating = Boolean(impersonatedEmail && impersonatedEmail !== currentUser?.email)

  // Use the same list_all_users admin action that appwriteQueryFns.profile.client
  // uses — it queries Appwrite Users API directly and is more reliable than the
  // synced users collection (which may not have every user).
  const impersonatedUserQuery = useQuery({
    queryKey: queryKeys.users.byEmail(isImpersonating ? impersonatedEmail : null),
    queryFn: async () => {
      const adminEmail = currentUser?.email || 'brainiac@brainiacsbots.com'
      const response = await brainiacsService.customAction('list_all_users', {
        admin_email: adminEmail,
      })
      if (!response.success || !Array.isArray(response.data)) return null
      const email = (impersonatedEmail || '').toLowerCase().trim()
      return response.data.find(
        (u: any) => String(u.email || '').toLowerCase().trim() === email
      ) ?? null
    },
    enabled: isImpersonating,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })

  const impersonatedUid: string | null = impersonatedUserQuery.data?.$id ?? null
  const impersonatedLabels: string[] = useMemo(() => {
    // Primary: labels from the list_all_users response
    const fromApi = impersonatedUserQuery.data?.labels
    if (Array.isArray(fromApi) && fromApi.length > 0) return fromApi
    // Fallback: roles from the team_members list (returned by N8N proxy, no extra call)
    const fromTeam = campaignsQuery.teamMembers.find(
      m => m.email.toLowerCase().trim() === (impersonatedEmail || '').toLowerCase().trim()
    )
    return fromTeam?.roles ?? []
  }, [impersonatedUserQuery.data, campaignsQuery.teamMembers, impersonatedEmail])

  // Resolve which labels govern Team View — the impersonated user's when impersonating,
  // otherwise the admin's.
  const effectiveIsTeamAdmin = isImpersonating
    ? impersonatedLabels.includes('teamadmin') || impersonatedLabels.includes('superadmin')
    : isTeamAdmin

  // Campaigns-view visibility rules (Manuel, 2026-09-04):
  //   1. Show campaigns assigned to me (account_manager_id === my Appwrite uid —
  //      n8n stamps the creator on teams with the outreach module; Team View
  //      reassignment updates it).
  //   2. account_manager_id NULL (solo clients without a team / stamp failures)
  //      → visible ONLY to users holding the team `owner` role.
  //   3. Anything else → not in Campaigns view. Team admins see the full
  //      roster in Team view instead (existing behavior, untouched).
  // Regular users without a team roster keep the fetcher-side server filter.
  const myCampaigns = useMemo(() => {
    const all = campaignsQuery.data ?? []
    const isAdminContext = campaignsQuery.teamMembers.length > 0

    const hasOwnerRole = (memberUid?: string | null, memberEmail?: string | null) => {
      const email = memberEmail?.toLowerCase().trim()
      return campaignsQuery.teamMembers.some((m) => {
        if (!m.roles?.includes('owner')) return false
        return (memberUid && m.user_id === memberUid)
          || (email && m.email?.toLowerCase().trim() === email)
      })
    }

    // Shared rule set, parameterized by the "viewer" (real or impersonated user).
    const filterFor = (viewerUid: string, viewerEmail: string | null, canSeeUnassigned: boolean) =>
      all.filter((c) => {
        if (c.account_manager_id === viewerUid) return true
        if (!c.account_manager_id) return canSeeUnassigned
        return false
      })

    if (isImpersonating) {
      // Wait until identity resolves before filtering — without a uid the
      // filter would always produce 0.
      if (!impersonatedUid) return all
      return filterFor(
        impersonatedUid,
        impersonatedEmail ?? null,
        hasOwnerRole(impersonatedUid, impersonatedEmail)
      )
    }

    if (!isAdminContext) return all

    const uid = currentUser?.uid
    if (!uid) return all
    return filterFor(
      uid,
      currentUser?.email ?? null,
      hasOwnerRole(uid, currentUser?.email)
    )
  }, [campaignsQuery.data, campaignsQuery.teamMembers, isImpersonating, currentUser?.uid, currentUser?.email, impersonatedEmail, impersonatedUid])

  // Derive counts from campaigns data (no separate N8N call)
  const campaignCounts = useCampaignCounts(myCampaigns)

  // Derive states from queries
  const derivedLeadsCount = campaignCounts.total
  const derivedClientInfo = clientQuery.data
  const derivedAllCampaignIds = Object.keys(campaignCounts.campaigns)

  const syncLeadStatusInCampaignCache = useCallback(
    (campaignId: string, leadId: string, nextStatus: string | undefined) => {
      if (!nextStatus?.trim()) return

      const syncCacheKey = (cacheKey: readonly unknown[]) => {
        queryClient.setQueryData<LeadCardData[] | undefined>(cacheKey, (existingLeads) => {
          if (!existingLeads) return existingLeads

          return existingLeads.map((item) => {
            if (String(item.id) !== leadId) return item

            return {
              ...item,
              status: getPreferredStatus(item.status, nextStatus) || nextStatus,
            }
          })
        })
      }

      syncCacheKey(queryKeys.leads.byCampaign(campaignId))
      syncCacheKey(queryKeys.leads.byCampaign(campaignId, targetEmail))
    },
    [queryClient, targetEmail]
  )

  // Gate only on shell essentials when we have NO data to show. Once a query
  // has resolved at least once, its cached/previous data stays visible through
  // re-fetches (placeholderData + keepPreviousData), so the user never sees a
  // blank spinner on network hiccup. The isPending + !data pattern means:
  // "show spinner only when there's nothing to show yet."
  //
  // NOTE: clientQuery (credits) is intentionally excluded — get_credit_balance
  // rides the n8n-proxy, and self-hosted Appwrite's synchronous executions
  // can 408-timeout after 30s. Credits are header polish; the campaigns list
  // must never wait on them (the header shows its own loading spinner).
  //
  // NOTE: prefsQuery is also excluded — it toggles its query key when userId
  // resolves ('' → actual uid), starting a new isPending query that would
  // keep the loading screen visible for no reason. Preferences are
  // non-critical UI polish (theme, effects) and should never block the list.
  const isInitialLoading =
    isCheckingRegistration && !isRegistered && !isRegistrationCheckFailed
  // True until the campaigns query has successfully resolved. Using
  // !isSuccess (not just isPending) so the skeleton holds through the
  // disabled → enabled → fetching → resolved window, and never flashes the
  // "no campaigns" empty state before we definitively know the result.
  const isLoadingCampaigns = !campaignsQuery.isSuccess

  // The registration form lives at its own URL (/register). New users are
  // redirected there instead of the form being embedded here, so every view —
  // including onboarding — has a shareable URL. The registration-status query
  // caches false for 5 min; /register invalidates it on completion so this
  // gate flips open without bouncing the user back.
  // Only treat as new user when the check DEFINITIVELY said false. On transient
  // failure the query errors and cached `false` must not hijack a registered
  // user into /register — that's the "onboarding view appears out of nowhere"
  // bug. (Cache keeps the last successful value while erroring.)
  const isNewUser =
    !impersonatedEmail && !isCheckingRegistration && !isRegistered && !isRegistrationCheckFailed

  useEffect(() => {
    if (isNewUser) {
      navigate({ to: '/register', replace: true })
    }
  }, [isNewUser, navigate])

  // Restore scroll position on reconnect + data ready.
  // Use a composite key so position is preserved per top-level tab.
  useScrollRestore(`campaigns-${tab ?? 'default'}`, !!myCampaigns?.length, viewMode !== 'team')

  // NOTE: no leads prefetch on the campaigns LIST view. Earlier versions
  // eagerly called fetchLeadsBundleFromN8N here (last-campaign prefetch +
  // viewport prefetch), which downloaded the leads_cards bundle for ALL
  // campaigns (~9 MB / 800+ leads) on page load. The list view must only
  // trigger campaign_metrics (useCampaigns); leads load when the user
  // actually opens a campaign.

  // Handle lead selection - navigate to nested lead route
  // Pre-fetch lead data for cache, then navigate (the route will use cached data)
  const handleSelectLead = useCallback(
    async (lead: LeadCardData) => {
      if (!targetEmail) return
      syncLeadStatusInCampaignCache(lead.campaignId, String(lead.id), lead.status)
      navigate({
        to: '/campaigns/$campaignId/leads/$leadId',
        params: { campaignId: lead.campaignId, leadId: String(lead.id) },
      })

      // Warm the detail cache in the background without blocking navigation or the rest of the UI.
      void queryClient.prefetchQuery({
        queryKey: queryKeys.leads.detail(lead.campaignId, String(lead.id)),
        queryFn: async () => {
          const fullLead = await appwriteQueryFns.leads.detail({
            campaignId: lead.campaignId,
            leadId: String(lead.id),
            userEmail: targetEmail,
          })

          return {
            ...fullLead,
            status: getPreferredStatus(lead.status, fullLead?.status) || fullLead?.status,
          }
        },
        staleTime: 10 * 60 * 1000,
      }).catch((error) => {
        console.error('Error prefetching lead:', error)
      })
    },
    [navigate, targetEmail, queryClient, syncLeadStatusInCampaignCache]
  )

  const hasNoCredits = !clientQuery.isPending && (derivedClientInfo?.credits ?? 0) <= 0

  // Handle add campaign navigation
  const handleAddCampaign = useCallback(() => {
    if (hasNoCredits) {
      setIsBuyCreditsModalOpen(true)
      return
    }

    navigate({ to: '/campaigns/create/form' })
  }, [hasNoCredits, navigate])

  const handleAddCredits = useCallback(() => {
    setIsBuyCreditsModalOpen(true)
  }, [])

  // Handle campaign selection - navigate to campaign page
  const handleSelectCampaign = useCallback(
    (campaignId: string) => {
      navigate({
        to: '/campaigns/$campaignId',
        params: { campaignId },
        search: { view: viewMode },
      })
    },
    [navigate, viewMode]
  )

  return (
    <AppLayout
      currentView="campaigns"
      onGoHome={() => setCampaignIndexKey((prev) => prev + 1)}
      onBuyCredits={() => setIsBuyCreditsModalOpen(true)}
      onViewInvoices={() => setIsInvoicesModalOpen(true)}
      aiCredits={derivedClientInfo?.credits ?? 0}
      creditsLoading={clientQuery.isPending}
      isBootLoading={isInitialLoading}
      creditsRefreshing={clientQuery.isFetching && !clientQuery.isPending}
      isTeamAdmin={effectiveIsTeamAdmin}
      teamMembersCount={campaignsQuery.teamMembers.length}
      onToggleTeamView={() => {
        const nextView = viewMode === 'team' ? 'campaigns' : 'team'
        navigate({
          to: '/campaigns',
          search: { view: nextView, layout, tab, ln, v, m, c },
          replace: true,
        })
      }}
      showTeamView={viewMode === 'team'}
    >
      {/* Initial Loading State — only shown when we have NO data to display.
          Absolutely positioned so it overlays (not pushes) the content below,
          then fades out via CSS transition when loading completes. */}
      <LoaderOverlay visible={isInitialLoading}>
        <BrainiacsLoader variant="fullscreen" size="lg" messages={LOADING_MESSAGES} subtitle="One moment please..." />
      </LoaderOverlay>

      {/* Main Content — always rendered so the skeleton is visible behind the
          loading overlay. Rises + fades in (700ms expo-out) as the overlay
          dissolves, layering with the GSAP liquid blur-in underneath. */}
      <main
          ref={stageRef}
          className={`app-page-shell app-page-offset max-w-[1600px] mx-auto px-6 py-4 pt-28 transition-[opacity,transform] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none ${isInitialLoading ? 'translate-y-6 opacity-0' : 'translate-y-0 opacity-100'}`}
          data-view-block
        >
          {viewMode === 'team' && effectiveIsTeamAdmin ? (
            <TeamWorkspace
              key={targetEmail}
              campaigns={campaignsQuery.data ?? []}
              teamMembers={campaignsQuery.teamMembers}
              teamInvitations={campaignsQuery.teamInvitations}
              isLoading={campaignsQuery.isLoading}
              error={campaignsQuery.isError ? 'Could not load team campaigns. Try again.' : null}
              onRetry={() => void campaignsQuery.refetch()}
              activeTab={teamTab || 'campaigns'}
              onTabChange={(next) => navigate({ to: '/campaigns', search: { view: 'team', teamTab: next, tab, layout, ln, v, m, c } })}
              onOpenCampaign={handleSelectCampaign}
              onAddCampaign={handleAddCampaign}
              onGenerateReport={(cid) => navigate({ to: '/reports/generate', search: { campaignId: cid, from: viewMode === 'team' ? '/campaigns?view=team' : '/campaigns' } })}
              availableCredits={derivedClientInfo?.credits ?? null}
              onAddCredits={handleAddCredits}
            />
          ) : (
            <CampaignIndex
              key={campaignIndexKey}
              leadsCount={derivedLeadsCount}
              clientInfo={derivedClientInfo}
              availableCredits={derivedClientInfo?.credits ?? 0}
              onSelectLead={handleSelectLead}
              onSelectCampaign={handleSelectCampaign}
              allCampaignIds={derivedAllCampaignIds}
              campaigns={myCampaigns}
              isLoadingCampaigns={isLoadingCampaigns}
              onNavigateHome={() => setCampaignIndexKey((prev) => prev + 1)}
              onAddCredits={handleAddCredits}
              onAddCampaign={handleAddCampaign}
              teamCampaignsAvailable={effectiveIsTeamAdmin}
              onSwitchToTeamView={() =>
                navigate({ to: '/campaigns', search: { view: 'team', layout, tab, ln, v, m, c }, replace: true })
              }
              // URL-driven view state. Every view/subview change routes through
              // onChangeRouter so the URL stays in sync and is shareable.
              activeTab={tab}
              onChangeTab={(next) =>
                // Spread the FULL current search state so switching the top-level
                // tab doesn't wipe the UniBox params (ln/v/m/c/layout).
                navigate({ to: '/campaigns', search: { view, tab: next, layout, ln, v, m, c }, replace: true })
              }
              // Leads layout deep-link (?layout=list|grid|cards). Absent → the
              // component's persisted preference applies.
              leadsLayout={layout ? LAYOUT_MAP[layout] : undefined}
              openISRLeadId={openLeadId}
              openISRCampaignId={openCampaignId}
              onChangeLeadsLayout={(next) => {
                const token = (Object.keys(LAYOUT_MAP) as Array<keyof typeof LAYOUT_MAP>).find(
                  (k) => LAYOUT_MAP[k] === next
                )
                navigate({
                  to: '/campaigns',
                  search: { view, tab, layout: token, ln, v, m, c },
                  replace: true,
                })
              }}
              uniboxLens={ln}
              uniboxView={v}
              uniboxMailbox={m}
              uniboxCalRange={c}
              onChangeUniBox={(next) =>
                navigate({
                  to: '/campaigns',
                  // Spread the FULL current search state (view/tab/ln/v/m/c) so a
                  // single-field update never wipes the others. goLens in UniBox
                  // fires 2-3 of these (lens + view + mailbox) in one click; with
                  // a partial object each call closed over STALE ln/v/m and they
                  // overwrote each other, forcing a second click to converge.
                  search: { view, tab, layout, ln, v, m, c, ...next },
                  replace: true,
                })
              }
              // BATCHED view-state change: ONE navigate per user action carrying
              // the full patch (lens + view + mailbox). This is the fix for the
              // "click a lens tab 2-6 times before it changes" bug — previously
              // goLens emitted separate per-field navigates that each closed over
              // stale search params and clobbered each other (last write won,
              // reverting the lens). Spreading current search + patch in a single
              // navigate makes multi-field changes atomic.
              onUniBoxChangeBatch={(patch) => {
                const nextLn = patch.lens ?? ln;
                const nextV = patch.view ?? v;
                const nextM = patch.mailbox ?? m;
                const nextC = patch.calRange ?? c;
                navigate({
                  to: '/campaigns',
                  search: { view, tab, layout, ln: nextLn, v: nextV, m: nextM, c: nextC },
                  replace: true,
                });
              }}
            />
          )}
        </main>

      {/* Buy Credits Modal */}
      <BuyCreditsModal
        isOpen={isBuyCreditsModalOpen}
        onClose={() => setIsBuyCreditsModalOpen(false)}
        title={hasNoCredits ? 'Add Credits To Create A Campaign' : undefined}
        subtitle={
          hasNoCredits
            ? 'You need credits before starting a new campaign. Top up now and come back ready to launch without redoing the setup.'
            : undefined
        }
      />

      {/* Invoices Modal */}
      <InvoicesModal isOpen={isInvoicesModalOpen} onClose={() => setIsInvoicesModalOpen(false)} />
          </AppLayout>
  )
}

/**
 * Contextual messages cycled by the BrainiacsLoader overlay while auth,
 * user profile, and campaigns queries resolve. Mix of real progress updates
 * and playful Brainiacs-voiced beats — long enough that users rarely see the
 * same sequence twice, short enough that every line stays readable at 2s.
 */
const LOADING_MESSAGES = [
  'Connecting to Brainiacs AI Engine',
  'Waking up your AI workers',
  'Loading your campaigns',
  'Sharpening the intelligence briefings',
  'Checking privileges and permissions',
  'Counting credits (all of them, promise)',
  'Securing your connection',
  'Teaching the owls new tricks',
  'Scanning pipelines for opportunities',
  'Polishing every pixel',
  'Aligning the outreach constellations',
  'Preparing your workspace',
  'Almost there — nice things loading',
] as const
